import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API_BASE, timeout: 20000 });

// Public
export const submitLead = (payload) => api.post('/leads', payload).then((r) => r.data);
export const submitTestimonial = (payload) => api.post('/testimonials', payload).then((r) => r.data);
export const fetchTestimonials = () => api.get('/testimonials').then((r) => r.data);
export const fetchCaseStudies = () => api.get('/case-studies').then((r) => r.data);
export const fetchCaseStudy = (slug) => api.get(`/case-studies/${slug}`).then((r) => r.data);
export const fetchPricing = (currency) => api.get('/pricing', { params: { currency } }).then((r) => r.data);
export const fetchCurrencies = () => api.get('/pricing/currencies').then((r) => r.data);

// Blog
export const fetchBlogPosts = (tag) => api.get('/blog', { params: tag ? { tag } : {} }).then((r) => r.data);
export const fetchBlogPost = (slug) => api.get(`/blog/${slug}`).then((r) => r.data);
export const adminListBlog = () => api.get('/admin/blog', adminHeaders()).then((r) => r.data);
export const adminCreateBlog = (payload) => api.post('/admin/blog', payload, adminHeaders()).then((r) => r.data);
export const adminUpdateBlog = (id, patch) => api.put(`/admin/blog/${id}`, patch, adminHeaders()).then((r) => r.data);
export const adminDeleteBlog = (id) => api.delete(`/admin/blog/${id}`, adminHeaders()).then((r) => r.data);
export const adminGenerateBlog = (topic) => api.post('/admin/blog/generate', { topic }, { ...adminHeaders(), timeout: 120000 }).then((r) => r.data);
export const subscribeNewsletter = (email) => api.post('/newsletter/subscribe', { email }).then((r) => r.data);
export const adminListSubscribers = () => api.get('/newsletter/admin/list', adminHeaders()).then((r) => r.data);
export const fetchYoutubeVideos = () => api.get('/youtube/videos').then((r) => r.data);

// Employee stories & campus drives
export const fetchEmployeeStories = () => api.get('/employee-stories').then((r) => r.data);
export const submitCampusDrive = (payload) => api.post('/campus-drive', payload).then((r) => r.data);
export const adminListStories = () => api.get('/admin/employee-stories', adminHeaders()).then((r) => r.data);
export const adminCreateStory = (payload) => api.post('/admin/employee-stories', payload, adminHeaders()).then((r) => r.data);
export const adminUpdateStory = (id, patch) => api.put(`/admin/employee-stories/${id}`, patch, adminHeaders()).then((r) => r.data);
export const adminDeleteStory = (id) => api.delete(`/admin/employee-stories/${id}`, adminHeaders()).then((r) => r.data);
export const adminListCampusDrives = () => api.get('/admin/campus-drives', adminHeaders()).then((r) => r.data);
export const adminUpdateCampusDrive = (id, status) => api.patch(`/admin/campus-drives/${id}`, { status }, adminHeaders()).then((r) => r.data);

// Meetings
export const fetchMeetingSlots = (dateStr) =>
  api.get('/meetings/slots', { params: { date: dateStr } }).then((r) => r.data);
export const bookMeeting = (payload) => api.post('/meetings', payload).then((r) => r.data);
export const adminListMeetings = () =>
  api.get('/admin/meetings', adminHeaders()).then((r) => r.data);
export const adminUpdateMeetingStatus = (id, status) =>
  api.patch(`/admin/meetings/${id}`, { status }, adminHeaders()).then((r) => r.data);

// Chat
export const sendChatMessage = (payload) => api.post('/chat', payload).then((r) => r.data);
export const fetchChatHistory = (sessionId) =>
  api.get('/chat/history', { params: { session_id: sessionId } }).then((r) => r.data);
export const adminListChatSessions = () =>
  api.get('/admin/chat/sessions', adminHeaders()).then((r) => r.data);
export const adminFetchChatSession = (id) =>
  api.get(`/admin/chat/sessions/${id}`, adminHeaders()).then((r) => r.data);
export const adminDeleteChatSession = (id) =>
  api.delete(`/admin/chat/sessions/${id}`, adminHeaders()).then((r) => r.data);

// Team / Consultants
export const adminListConsultants = () =>
  api.get('/admin/consultants', adminHeaders()).then((r) => r.data);
export const adminCreateConsultant = (payload) =>
  api.post('/admin/consultants', payload, adminHeaders()).then((r) => r.data);
export const adminUpdateConsultant = (id, patch) =>
  api.patch(`/admin/consultants/${id}`, patch, adminHeaders()).then((r) => r.data);
export const adminDeleteConsultant = (id) =>
  api.delete(`/admin/consultants/${id}`, adminHeaders()).then((r) => r.data);

// Admin
const adminHeaders = () => ({ headers: { 'X-Admin-Token': localStorage.getItem('hipro_admin_token') || '' } });
export const adminListLeads = () => api.get('/admin/leads', adminHeaders()).then((r) => r.data);
export const adminListTestimonials = () => api.get('/admin/testimonials', adminHeaders()).then((r) => r.data);
export const adminApproveTestimonial = (id, approved) =>
  api.patch(`/admin/testimonials/${id}`, { approved }, adminHeaders()).then((r) => r.data);
export const adminDeleteTestimonial = (id) =>
  api.delete(`/admin/testimonials/${id}`, adminHeaders()).then((r) => r.data);

// Country -> currency helper (client-side geo)
export const detectCurrency = async () => {
  try {
    const res = await fetch('https://ipapi.co/json/', { cache: 'no-store' });
    if (!res.ok) throw new Error('geo failed');
    const j = await res.json();
    const code = (j.country_code || '').toUpperCase();
    const map = { US: 'USD', GB: 'GBP', UK: 'GBP', SG: 'SGD', IN: 'INR' };
    if (map[code]) return map[code];
    const eu = ['IE','FR','DE','IT','ES','NL','BE','PT','AT','FI','GR','LU','SK','SI','EE','LV','LT','CY','MT'];
    if (eu.includes(code)) return 'EUR';
    return 'USD';
  } catch (e) {
    return 'USD';
  }
};

// IP-based geolocation (silent, no permission prompt)
export const detectGeo = async () => {
  try {
    const res = await fetch('https://ipapi.co/json/', { cache: 'no-store' });
    if (!res.ok) throw new Error('geo failed');
    const j = await res.json();
    if (typeof j.latitude !== 'number' || typeof j.longitude !== 'number') return null;
    return { lat: j.latitude, lng: j.longitude, city: j.city || '' };
  } catch (e) {
    return null;
  }
};

export default api;
